/**
 * "Back To Applications" origin tracking for the v2 application-detail and
 * combined-report pages.
 *
 * Every list surface (property applications + All Applications, v2/v3 AND the
 * legacy pages) keeps ALL of its filter + pagination state in the URL query
 * string, so a single encoded `pathname + search` fully reconstructs where the
 * user came from. We stamp that origin as `?returnTo=` when entering the detail
 * page, PRESERVE it across every applicant switch / individual↔combined toggle,
 * and the two back buttons navigate to it — validated same-origin via
 * `safeRedirect` so it can never be an open redirect.
 *
 * When there is NO remembered origin (an email/direct link, a fresh tab, or a
 * switch chain that started from one) back falls to the All Applications list
 * (client 2026-07-24 — supersedes the 2026-07-17 "always the property list"
 * decision; the email flow must land on All Applications, never bounce out of
 * the app the way `history.back()` would).
 */
import { safeRedirect } from 'remix-utils/safe-redirect'

export const RETURN_TO_PARAM = 'returnTo'

/** No in-app origin → the cross-property All Applications list. */
export const DEFAULT_BACK_TARGET = '/landlord/applications'

// A relative href like `/properties/…/v2` has no host; parse it against a dummy
// base so URLSearchParams does the query encoding/merging for us.
const DUMMY_BASE = 'http://localhost'

/**
 * Append `?returnTo=<origin>` to a detail href. `origin` is the CURRENT list
 * location (`pathname + search`); URLSearchParams encodes it into one param so
 * the detail URL stays valid. Falsy origin → the href unchanged. An href that
 * ALREADY carries a returnTo is left as-is — the closest origin wins and we
 * never stack them.
 */
export function appendReturnTo(
	href: string,
	origin: string | null | undefined,
): string {
	if (!origin) return href
	const url = new URL(href, DUMMY_BASE)
	if (url.searchParams.has(RETURN_TO_PARAM)) return href
	url.searchParams.set(RETURN_TO_PARAM, origin)
	return `${url.pathname}${url.search}`
}

/**
 * Carry the current page's returnTo onto a sibling detail href (applicant
 * switch, individual↔combined toggle). No-op when the current page has none,
 * so an email-entered chain never fabricates an origin.
 */
export function preserveReturnTo(
	href: string,
	searchParams: URLSearchParams,
): string {
	return appendReturnTo(href, searchParams.get(RETURN_TO_PARAM))
}

/**
 * The back-button target: the validated returnTo from the current search
 * params, else the All Applications fallback. `safeRedirect` guarantees a
 * same-origin relative path (rejects `//host`, `http://…`, non-strings).
 */
export function resolveBackTarget(
	searchParams: URLSearchParams,
	fallback: string = DEFAULT_BACK_TARGET,
): string {
	return safeRedirect(searchParams.get(RETURN_TO_PARAM), fallback)
}
